import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { LoaderCircle } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border font-medium leading-none no-underline transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'border-accent bg-accent-solid text-on-solid hover:border-accent hover:bg-accent-hover',
        default:
          'border-accent bg-accent-solid text-on-solid hover:border-accent hover:bg-accent-hover',
        secondary: 'border-border-default bg-inset text-primary hover:bg-hover',
        outline:
          'border-border-strong bg-transparent text-secondary hover:border-accent/50 hover:bg-inset/50 hover:text-primary',
        ghost:
          'border-transparent bg-transparent text-secondary hover:bg-inset/50 hover:text-primary',
        gradient:
          'border-transparent bg-gradient-to-r from-accent-solid to-accent-active text-on-solid hover:opacity-90',
        danger:
          'border-status-failed bg-status-failed-solid text-on-solid hover:bg-status-failed-solid',
        destructive:
          'border-status-failed bg-status-failed-solid text-on-solid hover:bg-status-failed-solid',
      },
      size: {
        small: 'min-h-8 px-3 text-xs',
        medium: 'min-h-10 px-4 text-sm',
        large: 'min-h-12 px-6 text-base',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  },
)

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingLabel?: string
  leadingIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      loading = false,
      loadingLabel = 'Working…',
      leadingIcon,
      className,
      children,
      disabled,
      variant,
      size,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        ref={ref}
        className={clsx(buttonVariants({ variant, size }), className)}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        type={asChild ? undefined : type}
        {...props}
      >
        {loading && (
          <LoaderCircle
            className="size-3.5 shrink-0 motion-safe:animate-spin"
            aria-hidden="true"
          />
        )}
        {leadingIcon && !loading && (
          <span className="grid shrink-0 place-items-center" aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        <Slottable>
          {asChild ? children : loading ? loadingLabel : children}
        </Slottable>
      </Component>
    )
  },
)

Button.displayName = 'Button'
